import React, { useState, forwardRef, useImperativeHandle } from 'react'
import moment from 'moment'
import { FaFileCsv, FaPlus } from 'react-icons/fa';
import { Button } from '@prosolve/common-components-ui'
import AddNewSessionModal from './AddNewSessionModal';
import { sessionsToEventsFormatter, eventsToSessionsFormatter, eventSessionFormatter } from './sessionEventUtils'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import UploadCsvSessionsModal from './UploadCsvSessionsModal';
import { getClientSubdomain } from '../../utils';

const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(Calendar);

export default forwardRef(({
  sessions: rawSessions = [],
  eventId,
  isReadOnly,
  defaultView
}, ref) => {

  // The way the data is managed, its importants to clone each object we are working with
  const initSessions = rawSessions.map(obj => ({ ...obj }))
  
  const clientSubdomain = getClientSubdomain()
  const isStarSubdomain = (clientSubdomain === 'star')

  const [sessions, setSessions] = useState(initSessions)

  const initEvents = sessionsToEventsFormatter(sessions)

  const [events, setEvents] = useState(initEvents)

  const [newSessions, setNewSessions] = useState([])

  // const [selectedEvent, setSelectedEvent] = useState()

  const [isAddNewSessionOpen, setIsAddNewSessionsOpen] = useState()
  const [isUploadCsvSessionsOpen, setIsUploadCsvSessionsOpen] = useState()

  const defaultDate = events[0] ? events[0].start : new Date()
  const [date, setDate] = useState(undefined)

  useImperativeHandle(ref, () => {
    return {
      getAllNonNewSessions() {
        return eventsToSessionsFormatter(sessions, events);
      },
      getModifiedSessions() {
        // Only get and return events marked dirty
        const dirtyEvents = events.filter(({ dirty }) => dirty === true)
        return eventsToSessionsFormatter(sessions, dirtyEvents);
      },
      getDeletedSessions() {
        const deletedEvents = [];
        sessions.forEach((session) => {
          const { _id } = session;
          const matchingEvent = events.find(({ id }) => id === _id);
          if (!matchingEvent) {
            deletedEvents.push(session)
          }
        });
        return deletedEvents;
      },
      getNewSessions() {
        const newEvents = [];
        events.forEach((event) => {
          //const { title } = event;
          //const matchingNewSession = newSessions.find(({ name }) => name === title);
          const { id: _id } = event;
          const matchingNewSession = newSessions.find(({ id }) => id === _id);
          if (matchingNewSession) {
            newEvents.push(eventSessionFormatter(matchingNewSession, event))
          }
        });
        newEvents.forEach(e => delete e.id)
        return newEvents;
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const onEventChange = (data) => {
    const { start, end, event: { id: eventId } } = data; 

    const startDayOfYear = moment(start).dayOfYear()
    const endDayOfYear = moment(end).dayOfYear()

    // Prevent update if start and end days are not the same
    if (startDayOfYear === endDayOfYear) {
      const eventsUpdate = [ ...events ];

      const eventToUpdateObj = eventsUpdate.find(({ id }) => id === eventId);

      if (eventToUpdateObj) {
        eventToUpdateObj.start = start;
        eventToUpdateObj.end = end;
        eventToUpdateObj.dirty = true;
      }

      setEvents(eventsUpdate)
    }
  };

  // const onEventSelected = (event) => {
  //   setSelectedEvent(event)
  // }

  const onEventSelected = (event) => {
    if (!isReadOnly) {
      onEditSession(event)
    }
  }

  const onAddEditSuccess = (newEventData, isUpdate, updateId) => {
    if (isUpdate) {
      onUpdateSession(newEventData, updateId)
    } else {
      onAddNewSession([newEventData])
    }
  }

  const onCsvSuccess = (sessions) => {
    const firstDate = (sessions?.[0] ? moment(`${sessions?.[0]?.date} ${sessions?.[0]?.startTime}`).toDate() : date)
    onAddNewSession(sessions, true)
    setDate(firstDate)
  }

  const onAddNewSession = (newEventDataArr, isCsv) => {
    const updateNewSessions = [ ...(isCsv ? [] : newSessions) ];
    const eventsUpdate = [ ...(isCsv ? [] : events) ];

    newEventDataArr.forEach(newEventData => {
      const id = `new-${crypto.randomUUID()}`

      newEventData.id = id
      updateNewSessions.push({ ...newEventData, eventId })
  
      const newEventObj = (sessionsToEventsFormatter([newEventData]))[0]
      newEventObj.id = id
      eventsUpdate.push(newEventObj)
    })

    setNewSessions(updateNewSessions)
    setEvents(eventsUpdate)

    setIsAddNewSessionsOpen(undefined)
  }

  const onUpdateSession = (updateEventData, updateId) => {
    const eventsUpdate = [ ...events ];

    const eventToUpdateObj = eventsUpdate.find(({ id }) => id === updateId);

    if (eventToUpdateObj) {
      const convertedEventsObj = (sessionsToEventsFormatter([updateEventData]))[0]
      eventToUpdateObj.start = convertedEventsObj.start;
      eventToUpdateObj.end = convertedEventsObj.end;
      eventToUpdateObj.title = convertedEventsObj.title;
      eventToUpdateObj.dirty = true;
    }

    setEvents(eventsUpdate)

    const sessionsUpdate = [ ...sessions ];

    const sessionToUpdateObj = (updateId.indexOf('new-') > -1) ?
      newSessions.find(({ id }) => id === updateId) :
      sessionsUpdate.find(({ _id }) => _id === updateId);

    if (sessionToUpdateObj) {
      sessionToUpdateObj.name = updateEventData.name;
      sessionToUpdateObj.moduleBlueprintId = updateEventData.moduleBlueprintId || '';
      sessionToUpdateObj.image = updateEventData.image || '';
    }

    setSessions(sessionsUpdate)

    setIsAddNewSessionsOpen(undefined)
  }

  const onEditSession = (selectedEvent) => {
    if (selectedEvent) {
      const fullEventData = eventsToSessionsFormatter([...sessions, ...newSessions], [selectedEvent])[0]

      if (fullEventData && selectedEvent.id && selectedEvent.id.indexOf('new-') > -1) {
        fullEventData._id = selectedEvent.id;
      }

      setIsAddNewSessionsOpen(fullEventData)
    }
  }

  const onDeleteSession = (selectedEventFullObj = {}) => {
    const selectedEvent = events.find(({ id }) => id === selectedEventFullObj._id);

    if (selectedEvent) {
      const updateEvents = [...events]
      const deleteIndex = updateEvents.findIndex(({ id }) => id === selectedEvent.id)

      if (deleteIndex > -1) {
        updateEvents.splice(deleteIndex, 1)

        setEvents(updateEvents)
        // setSelectedEvent(undefined)
      }
    }
    setIsAddNewSessionsOpen(undefined)
  }

  return (
    <>
      {
        Boolean(isAddNewSessionOpen) &&
        <AddNewSessionModal
          existingSessionData={isAddNewSessionOpen}
          onClose={() => setIsAddNewSessionsOpen(undefined)}
          onSuccess={onAddEditSuccess}
          onDeleteSession={onDeleteSession}
        />
      }
      {
        Boolean(isUploadCsvSessionsOpen) &&
        <UploadCsvSessionsModal 
          onClose={() => setIsUploadCsvSessionsOpen(undefined)}
          onSuccess={onCsvSuccess}
        />
      }
      {
        !isReadOnly &&
        <div style={{
          position: 'absolute',
          top: '0px',
          right: '300px',
          display: 'flex',
          columnGap: '5px'
        }}>
          <Button
            style={{ 
              // display: 'inline-block'
              height: '19px',
              border: 'none'
            }}
            onClick={() => setIsAddNewSessionsOpen({})}
          >
            <FaPlus style={{ color: '#0280c6' }} />
            {/* <span style={{ color: '#565656' }}>{'Add New Session'}</span> */}
          </Button>
          { 
            isStarSubdomain && 
            <Button
              style={{ height: '19px', border: 'none' }}
              onClick={() => setIsUploadCsvSessionsOpen({})}
            >
              <FaFileCsv style={{ color: '#0280c6' }} />
            </Button>
          }
          {/* <Button
            style={{ 
              height: '19px',
              border: 'none'
            }}
            onClick={onEditSession}
          >
            <FaPen style={{ color: '#0280c6' }} />
          </Button> */}
          {/* <Button
            style={{ 
              height: '19px',
              border: 'none'
            }}
            onClick={onDeleteSession}
          >
            <FaTrash style={{ color: '#ef0c0c' }} />
          </Button> */}
        </div>
      }
      {
        isReadOnly ?
        <Calendar
          localizer={localizer}
          events={events}
          defaultDate={defaultDate}
          date={date}
          onNavigate={(newDate) => setDate(newDate)}
          defaultView={defaultView || 'week'}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 'calc(100vh - 130px)' }}
          onEventDrop={onEventChange}
          onEventResize={onEventChange}
          onSelectEvent={onEventSelected}
          popup
          resizable
        />
        :
        <DnDCalendar
          localizer={localizer}
          events={events}
          defaultDate={defaultDate}
          date={date}
          onNavigate={(newDate) => setDate(newDate)}
          defaultView={defaultView || 'week'}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 'calc(100vh - 130px)' }}
          onEventDrop={onEventChange}
          onEventResize={onEventChange}
          onSelectEvent={onEventSelected}
          popup
          resizable
        />
      }
    </>
  )
})